import { useEffect, useRef } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { homePromotions, promotionReleaseLine } from '../../data/homePromotions'
import { ANALYTICS_CONSENT_EVENT, trackEvent } from '../../services/analytics'
import './homePromotions.css'

export function HomePromotions() {
  const element = useRef<HTMLElement>(null)
  const viewed = useRef(new Set<string>())

  useEffect(() => {
    const cards = element.current?.querySelectorAll<HTMLElement>('[data-promotion-id]')
    if (!cards || !('IntersectionObserver' in window)) return
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const id = (entry.target as HTMLElement).dataset.promotionId
        if (entry.isIntersecting && id && !viewed.current.has(id) && trackEvent('promotion_view', { campaign: id, placement: 'home_after_top_stories' })) {
          viewed.current.add(id)
          observer.unobserve(entry.target)
        }
      }
    }, { threshold: 0.35 })
    const observe = () => cards.forEach((card) => {
      if (!viewed.current.has(card.dataset.promotionId!)) {
        observer.unobserve(card)
        observer.observe(card)
      }
    })
    observe()
    window.addEventListener(ANALYTICS_CONSENT_EVENT, observe)
    return () => { observer.disconnect(); window.removeEventListener(ANALYTICS_CONSENT_EVENT, observe) }
  }, [])

  return <aside className="home-promotions" aria-label="Advertisements" ref={element} data-analytics-section="home_advertisements">
    <div className="home-promotions__label">ADVERTISEMENT</div>
    <div className="home-promotions__grid">
      {homePromotions.map((promotion) => {
        const releaseLine = promotionReleaseLine(promotion)
        const click = (channel: string) => trackEvent('promotion_click', { campaign: promotion.id, placement: 'home_after_top_stories', channel })
        return <section className={`home-promotion home-promotion--${promotion.theme}`} key={promotion.id} data-promotion-id={promotion.id} aria-labelledby={`promotion-${promotion.id}`}>
          <div className="home-promotion__disclosure">{promotion.disclosure}</div>
          <a className="home-promotion__art" href={promotion.destination} target="_blank" rel="sponsored noopener noreferrer" aria-label={promotion.action} onClick={() => click('image')}>
            <img src={promotion.image} alt={promotion.imageAlt} width={promotion.imageWidth} height={promotion.imageHeight} loading="lazy" decoding="async"/>
          </a>
          <div className="home-promotion__copy">
            <span className="home-promotion__eyebrow">{promotion.eyebrow}</span>
            <h3 id={`promotion-${promotion.id}`}>{promotion.headline}</h3>
            {releaseLine && <p className="home-promotion__release">{releaseLine}</p>}
            <p className="home-promotion__body">{promotion.body}</p>
            {promotion.imageCredit && <small className="home-promotion__credit">{promotion.imageCredit}</small>}
            <div className="home-promotion__action">
              {promotion.affiliateDisclosure && <p className="home-promotion__commission">{promotion.affiliateDisclosure}</p>}
              <a href={promotion.destination} target="_blank" rel="sponsored noopener noreferrer" onClick={() => click('cta')}>{promotion.action}<ArrowUpRight aria-hidden="true"/></a>
            </div>
          </div>
        </section>
      })}
    </div>
  </aside>
}
